import Handlebars from 'handlebars';

const DECIMAL_PORTION_REGEX = /([.,]\d{1,2})(?=\D*$)/;
const DEFAULT_PERCENTAGE = 65;
const MIN_PERCENTAGE = 1;
const MAX_PERCENTAGE = 100;

export interface SplitFormattedDecimalResult {
  main: string;
  decimal: string;
  suffix: string;
}

/**
 * Splits a pre-formatted numeric/currency string into its main portion and its
 * decimal portion (decimal separator + 1-2 trailing digits), keeping any
 * trailing non-digit suffix (e.g. ` €`, ` CR`) separate.
 *
 * Returns `null` when no decimal portion is detected (e.g. `1,234` or `1234`).
 *
 * Locale-agnostic: detects the LAST `.` or `,` followed by 1-2 digits where the
 * remaining characters are non-digits to end of string. Avoids false positives
 * on thousands separators like `1.234` (de-DE) or `1,234` (en-GB).
 *
 * @example
 * splitFormattedDecimal('£1,234.56') // { main: '£1,234', decimal: '.56', suffix: '' }
 * splitFormattedDecimal('1.234,56 €') // { main: '1.234', decimal: ',56', suffix: ' €' }
 * splitFormattedDecimal('1,234') // null
 */
export function splitFormattedDecimal(value: string): SplitFormattedDecimalResult | null {
  const match = DECIMAL_PORTION_REGEX.exec(value);
  if (match?.index === undefined) {
    return null;
  }
  const decimal = match[1];
  const main = value.slice(0, match.index);
  const suffix = value.slice(match.index + decimal.length);
  return { main, decimal, suffix };
}

/**
 * Handlebars helper: renders the decimal separator + decimal digits of a
 * pre-formatted numeric string at a reduced font size, expressed as a
 * percentage of the parent font size. Uses CSS `round(<frac>em, 1px)` so the
 * browser scales relative to whatever font-size the parent has and rounds to
 * the nearest whole pixel without the helper needing to know that size.
 *
 * Other inherited font properties (weight, colour, family, line-height) are
 * preserved because only `font-size` is set inline.
 *
 * Input must already be formatted (e.g. via `currencyPipe` or
 * `localiseNumberFormat`). Values without a decimal portion are returned
 * unchanged.
 *
 * @example
 * {{shrinkDecimals (currencyPipe balance currencyCode='GBP') 65}}
 * {{shrinkDecimals formattedAmount 60}}
 * {{shrinkDecimals formattedAmount}} // defaults to 65%
 */
export function shrinkDecimalsHelper(
  value: string | number,
  percentage: string | number,
): Handlebars.SafeString | string {
  if (value === null || value === undefined || value === '') {
    return value as string;
  }

  const stringValue = coerceToString(value);

  if (stringValue.includes('skeleton_')) {
    return stringValue;
  }

  const split = splitFormattedDecimal(stringValue);
  if (!split) {
    return stringValue;
  }

  const pct = resolvePercentage(percentage);
  const fraction = formatFraction(pct / 100);
  const decimalEscaped = Handlebars.Utils.escapeExpression(split.decimal);
  const mainEscaped = Handlebars.Utils.escapeExpression(split.main);
  const suffixEscaped = Handlebars.Utils.escapeExpression(split.suffix);

  const html =
    `${mainEscaped}` +
    `<span style="font-size:round(${fraction}em,1px)">${decimalEscaped}</span>` +
    `${suffixEscaped}`;

  return new Handlebars.SafeString(html);
}

function resolvePercentage(percentage: string | number): number {
  const num = parsePercentageInput(percentage);
  if (!Number.isFinite(num)) {
    return DEFAULT_PERCENTAGE;
  }
  if (num < MIN_PERCENTAGE) return MIN_PERCENTAGE;
  if (num > MAX_PERCENTAGE) return MAX_PERCENTAGE;
  return num;
}

function parsePercentageInput(percentage: string | number): number {
  if (typeof percentage === 'number') return percentage;
  if (typeof percentage === 'string') return Number.parseFloat(percentage);
  return Number.NaN;
}

function coerceToString(value: string | number): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  // Unexpected types (objects/arrays) are not meaningful for a pre-formatted
  // numeric helper - treat as empty so the helper falls through unchanged.
  return '';
}

/**
 * Formats a 0-1 fraction for CSS `em`, trimming trailing zeros and a trailing
 * decimal point (e.g. 0.6, 0.65, 1).
 */
function formatFraction(fraction: number): string {
  // Round to 4 decimals to avoid floating-point noise like 0.6500000000000001.
  const rounded = Math.round(fraction * 10000) / 10000;
  return rounded
    .toString()
    .replace(/(\.\d*?)0+$/, '$1')
    .replace(/\.$/, '');
}
